import { api, setAuthToken, extractData } from './api';
import { User } from '../models/User';
import { getOrCreateSessionId } from './sessionId';

export type Credentials = {
    email: string;
    password: string;
    turnstile_token?: string;
    company_website?: string;
};
export type RegisterPayload = {
    name: string;
    email: string;
    password: string;
    password_confirmation: string;
    country_id?: number | null;
    turnstile_token?: string;
    company_website?: string;
};
export type OAuthProvider = 'google' | 'facebook';

const TOKEN_KEY = 'auth_token';

export function loadToken(): string | null {
    const token = localStorage.getItem(TOKEN_KEY);
    setAuthToken(token);
    return token;
}

export function saveToken(token: string) {
    localStorage.setItem(TOKEN_KEY, token);
    setAuthToken(token);
}

export function clearToken() {
    localStorage.removeItem(TOKEN_KEY);
    setAuthToken(null);
}

export async function register(payload: RegisterPayload): Promise<User> {
    const res = await api.post('/auth/register', {
        ...payload,
        session_id: getOrCreateSessionId(),
    });
    const data = extractData<{ token?: string; user?: any }>(res);
    const token = data.token;
    const userData = data.user;
    if (token) saveToken(token);
    if (!userData) throw new Error('User data not received');
    return new User(userData);
}

export async function login(credentials: Credentials): Promise<User> {
    const res = await api.post('/auth/login', {
        ...credentials,
        session_id: getOrCreateSessionId(),
    });
    const data = extractData<{ token?: string; user?: any }>(res);
    const token = data.token;
    const userData = data.user;
    if (!token) throw new Error('Token not received');
    saveToken(token);
    if (!userData) throw new Error('User data not received');
    return new User(userData);
}

export async function me(): Promise<User> {
    const res = await api.get('/auth/me');
    const data = extractData<{ user?: any }>(res);
    const userData = data.user;
    if (!userData) throw new Error('User data not received');
    return new User(userData);
}

export async function logout(): Promise<void> {
    try {
        await api.post('/auth/logout');
    } finally {
        clearToken();
    }
}

export async function updateProfile(data: {
    name?: string;
    email?: string;
    password?: string;
    current_password?: string;
    country_id?: number | null;
}): Promise<User> {
    const res = await api.put('/auth/profile', data);
    const responseData = extractData<{ user?: any }>(res);
    const userData = responseData.user;
    if (!userData) throw new Error('User data not received');
    return new User(userData);
}

export async function fetchOAuthProviders(): Promise<OAuthProvider[]> {
    const res = await api.get('/auth/oauth/providers');
    const data = extractData<{ providers?: OAuthProvider[] }>(res);
    return Array.isArray(data.providers) ? data.providers : [];
}

/**
 * Open provider OAuth in a popup. Backend handles the callback and postMessages
 * token + user back to this window.
 */
export async function oauthLogin(
    provider: OAuthProvider,
    options?: { countryId?: number | null }
): Promise<{ user: User; created: boolean }> {
    const res = await api.post(`/auth/oauth/${provider}/redirect`, {
        frontend_origin: window.location.origin,
        country_id: options?.countryId ?? undefined,
        session_id: getOrCreateSessionId(),
    });
    const data = extractData<{ auth_url?: string }>(res);

    if (!data.auth_url) {
        throw new Error('OAuth authorization URL not received');
    }

    const width = 560;
    const height = 720;
    const left = Math.max(0, window.screenX + (window.outerWidth - width) / 2);
    const top = Math.max(0, window.screenY + (window.outerHeight - height) / 2);

    const popup = window.open(
        data.auth_url,
        `oauth-${provider}`,
        `width=${width},height=${height},left=${left},top=${top},scrollbars=yes`
    );

    if (!popup) {
        throw new Error('Popup blocked. Please allow popups for this site.');
    }

    return new Promise<{ user: User; created: boolean }>((resolve, reject) => {
        let settled = false;

        const cleanup = () => {
            window.removeEventListener('message', messageHandler);
            clearInterval(checkPopup);
        };

        const finish = (fn: () => void) => {
            if (settled) return;
            settled = true;
            cleanup();
            try {
                popup.close();
            } catch {
                // ignore
            }
            fn();
        };

        const checkPopup = setInterval(() => {
            if (!settled && popup.closed) {
                finish(() => reject(new Error('OAuth popup was closed')));
            }
        }, 500);

        const messageHandler = (event: MessageEvent) => {
            if (event.origin !== window.location.origin) {
                // Backend bridge posts from API origin — accept configured API host too.
                const apiBase = process.env.REACT_APP_API_URL || 'http://127.0.0.1:8000';
                try {
                    if (event.origin !== new URL(apiBase).origin) {
                        return;
                    }
                } catch {
                    return;
                }
            }

            const payload = event.data;
            if (!payload || typeof payload !== 'object') {
                return;
            }

            if (payload.type === 'oauth-success') {
                finish(() => {
                    if (!payload.token || !payload.user) {
                        reject(new Error('OAuth authentication failed'));
                        return;
                    }
                    saveToken(payload.token);
                    resolve({
                        user: new User(payload.user),
                        created: Boolean(payload.created),
                    });
                });
            } else if (payload.type === 'oauth-error') {
                finish(() => {
                    reject(new Error(payload.error || 'OAuth authentication failed'));
                });
            }
        };

        window.addEventListener('message', messageHandler);
    });
}
